import documentDataStyle from './documentData.module.scss';

import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PathName, getRouteByKey } from 'utility/routes';
import Persist, { StorageKind } from 'lib/persist';
import { RootState, useAppDispatch } from 'lib/centralStore';
import { SelectInput, SelectOptionType } from 'components/inputs/selectInput';
import { SubmitHandler, useForm } from 'react-hook-form';
import { setDocumentData } from 'features/signup/signup';
import { stringNotEmpty, stringWithoutDigit } from 'lib/labels';

import { ButtonRegistrationWrapper } from '../buttonRegistrationWrapper';
import { DataInput } from 'components/inputs/dataInput';
import { REGISTRATION_STEP_KEY } from 'features/signup/constants';
import { RegistrationStatus } from 'components/page-content/registrati/PageContent';
import { RegistrationValue } from 'features/signup/types/signUpEnum';
import { RichText } from 'components/richText';
import { SignUpErrorResponse } from 'features/signup/types';
import { TextInput } from 'components/inputs/textInput';
import format from 'date-fns/format';
import styles from 'components/page-content/registrati/registrati.module.scss';
import subYears from 'date-fns/subYears';
import { useLazySignUpQuery } from 'features/api/userSlice';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { gtag, myUnidecode } from 'utility/functions';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const localStorage = Persist(StorageKind.local);

export type DocumentDataFormLabel = {
  titleDocumentDataFrom: string;
  descriptionDocumentDataForm: string;
  documentType: string;
  releasedBy: {
    [key: string]: Record<string, string>;
  } & { default: string };
  availableDocuments: Array<{
    label: string;
    value: string;
  }>;
  placeholderDocumentNumber: string;
  labelReleasedBy: string;
  placeholderLocationRelease: string;
  releaseDate: string;
  expirationDate: string;
  documentNumber: string;
  locationRelease: string;
};

export type DocumentDataFormProps = {
  back: Dispatch<SetStateAction<boolean>>;
  setState: Dispatch<SetStateAction<RegistrationStatus>>;
  labels: DocumentDataFormLabel;
  labelLastStep?: string;
};
export const DocumentDataForm: React.FC<DocumentDataFormProps> = ({ back, labels, setState, labelLastStep }) => {
  // const [isCallingRequest, setIsCallingRequest] = useState(false);
  const [isStepCompleted, setIsStepCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const dispatch = useAppDispatch();
  const schema = z.object({
    documentType: z.string().refine((value) => value !== 'default'),
    documentNumber: z.string().min(4),
    releasedBy: z
      .string()
      .min(1)
      .refine((value) => value !== 'default'),
    releasePlace: stringNotEmpty.pipe(stringWithoutDigit),
    releaseDate: z.string().min(1),
    expiresIn: z.string().min(1),
  });
  type SchemaValidation = z.infer<typeof schema>;
  const state = useSelector((store: RootState) => store.signup);
  const [listDocumentReleasedBy, setListDocumentReleasedBy] = useState<Array<SelectOptionType>>([
    { value: 'default', label: labels.releasedBy['default'], isDisabled: true },
  ]);

  const [trigger] = useLazySignUpQuery();
  const { asPath } = useRouter();
  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);

  const sendData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await trigger(state).unwrap();
      setState({ state: RegistrationValue.SUCCESS });
      if (response.cod_contratto) {
        localStorage.setItem('cod_contratto', response.cod_contratto);
      }
      setIsLoading(false);
    } catch (error) {
      const typedError: SignUpErrorResponse = error;
      setState({
        state: RegistrationValue.ERROR,
        description: typedError.data.response?.descrizione,
        esito: typedError.data.response?.esito,
      });
      setIsLoading(false);
    }
  }, [setState, state, trigger]);

  // Necessary to send update state
  useEffect(() => {
    if (isStepCompleted) {
      sendData();
    }
  }, [isStepCompleted, sendData]);

  useEffect(() => {
    if (asPath.includes(registrationUrl)) {
      const step = localStorage.getItem(REGISTRATION_STEP_KEY);
      if (step !== 'document-data') {
        localStorage.setItem(REGISTRATION_STEP_KEY, 'document-data');
        gtag(
          {
            section: 'RegistrationStepDocumentData',
            detail: 'RegistrationStepDocumentData',
            event: 'RegistrationStepDocumentData',
          },
          true
        );
      }
    }
  }, [registrationUrl, asPath]);

  const { control, handleSubmit, formState, setValue, watch } = useForm<SchemaValidation>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      documentNumber: state.documentNumber,
      documentType: state.triggerForm ? state.documentType : 'default',
      expiresIn: state.expirationDate,
      releaseDate: state.releaseDate,
      releasedBy: state.triggerForm ? state.releasedBy : 'default',
      releasePlace: state.releasePlace,
    },
  });

  useEffect(() => {
    if (state.triggerForm) {
      handleReleasedBy();
    }
  }, [state.triggerForm]);

  const goNext: SubmitHandler<SchemaValidation> = async (data) => {
    dispatch(
      setDocumentData({
        expirationDate: data.expiresIn,
        documentNumber: myUnidecode(data.documentNumber),
        documentType: data.documentType,
        releasePlace: myUnidecode(data.releasePlace),
        releaseDate: data.releaseDate,
        releasedBy: data.releasedBy,
      })
    );
    setIsStepCompleted(true);
  };

  const handleReleasedBy = (element?: ChangeEvent<HTMLSelectElement>) => {
    const documentType = state.triggerForm && !element ? state.documentType : element?.target.value;
    const documentTypeReleasedBy = labels.releasedBy[documentType!];
    const listOfKeys = Object.keys(documentTypeReleasedBy);
    const listOfReleasedBy: Array<SelectOptionType> = listOfKeys.map((keyElem) => {
      return {
        value: keyElem,
        label: documentTypeReleasedBy[keyElem],
      };
    });
    setListDocumentReleasedBy([
      {
        value: 'default',
        label: labels.releasedBy['default'],
        isDisabled: true,
      },
      ...listOfReleasedBy,
    ]);
    setValue('releasedBy', state.triggerForm ? state.releasedBy : 'default');
  };

  return (
    <div className={styles.formContainer}>
      <h2 className={styles.title}>{labels.titleDocumentDataFrom}</h2>
      <RichText containerClassName={styles.subTitle}>{labels.descriptionDocumentDataForm}</RichText>
      <form onSubmit={handleSubmit(goNext)} className={styles.form} ref={formRef}>
        <SelectInput
          control={control}
          name="documentType"
          label={labels.documentType}
          options={labels.availableDocuments.map((documents) => ({
            ...documents,
            isDisabled: documents.value === 'default',
          }))}
          onChange={(element) => handleReleasedBy(element)}
          hasMarginBottom
        />
        <TextInput
          control={control}
          name="documentNumber"
          label={labels.documentNumber}
          placeholder={labels.placeholderDocumentNumber}
          hasMarginBottom
        />
        <div className={documentDataStyle.inputRow}>
          <SelectInput
            control={control}
            name="releasedBy"
            label={labels.labelReleasedBy}
            options={listDocumentReleasedBy}
            hasMarginRight
            hasMarginBottom
          />
          <TextInput
            control={control}
            name="releasePlace"
            label={labels.locationRelease}
            placeholder={labels.placeholderLocationRelease}
            hasMarginBottom
          />
        </div>
        <div className={documentDataStyle.inputRow}>
          <DataInput
            control={control}
            name="releaseDate"
            label={labels.releaseDate}
            hasMarginRight
            hasMarginBottom
            min={format(subYears(new Date(new Date().getFullYear(), 0, 1), 20), 'yyyy-MM-dd')}
            max={format(new Date(), 'yyyy-MM-dd')}
          />
          <DataInput
            control={control}
            name="expiresIn"
            label={labels.expirationDate}
            placeholder={labels.placeholderLocationRelease}
            hasMarginBottom
            disabled={!watch().releaseDate}
            // min={format(addYears(new Date(watch().releaseDate || '1920'), 3), 'yyyy-MM-dd')}
            min={format(new Date(), 'yyyy-MM-dd')}
          />
        </div>
        <ButtonRegistrationWrapper
          handleGoBack={back!}
          isNextDisabled={!formState.isValid}
          lastStep={labelLastStep}
          isLoading={isLoading}
        ></ButtonRegistrationWrapper>
      </form>
    </div>
  );
};
